import React, { useState, useEffect } from "react";
import "./Navbar.css";
import Logo from "../../components/Logo";
import { navigations } from "../../data";
import { Link as ScrollLink } from "react-scroll";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { FiMenu, FiX } from "react-icons/fi"; // close icon
import Sidebar from "../../components/Sidebar";
import PhoneModal from "../../components/PhoneModal";
import useNavbar from "../../hook/useNavbar";
import i18n from "../../i18n";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie"; // Import Cookies for cookie handling

/* ===== Locale helpers ===== */
const getLangFromPath = (pathname) => (pathname.startsWith("/fr") ? "fr" : "en");

/** Return a localized version of a route path.
 *  - For FR: ensure path starts with /fr
 *  - For EN: strip any leading /fr
 */
const localeizePath = (lang, path) => {
  if (!path) return path;
  // external link? leave it
  if (/^https?:\/\//i.test(path)) return path;

  if (lang === "fr") {
    if (path.startsWith("/fr")) return path;
    if (path.startsWith("/")) return `/fr${path}`;
    return `/fr/${path}`;
  } else {
    // EN default: remove leading /fr (but keep the rest)
    const cleared = path.replace(/^\/fr(?=\/|$)/, "");
    return cleared.length ? cleared : "/";
  }
};

const Navbar = () => {
  const [openSidebar, setOpenSidebar] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const { showNavbar, isMobile, isInHero } = useNavbar();

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Language state (EN/FR) — default to localStorage, cookies, or URL path
  const [lang, setLang] = useState(() => {
    return localStorage.getItem("preferredLanguage") || Cookies.get("language") || getLangFromPath(location.pathname);
  });

  // Keep i18n + local state synced to the current URL
  useEffect(() => {
    const urlLang = getLangFromPath(location.pathname);
    if (i18n.language !== urlLang) i18n.changeLanguage(urlLang);
    if (lang !== urlLang) setLang(urlLang);
    
    // Close mobile menu when route changes
    setOpenSidebar(false);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLanguage = (code) => {
    setLang(code);
    i18n.changeLanguage(code);

    // Store the selected language in both localStorage and cookie
    localStorage.setItem("preferredLanguage", code);
    Cookies.set("language", code, { expires: 7 });

    // Rewrite current URL to include/remove /fr while preserving search/hash
    const { pathname, search, hash } = location;
    const safePath = (pathname || "/").replace(/\/{2,}/g, "/");
    const withoutFr = safePath.replace(/^\/fr(?=\/|$)/, "") || "/";
    const nextPath =
      code === "fr" ? (withoutFr === "/" ? "/fr" : `/fr${withoutFr}`) : withoutFr;

    navigate(`${nextPath}${search || ""}${hash || ""}`);
  };

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (openSidebar) document.body.classList.add("mm-lock");
    else document.body.classList.remove("mm-lock");
    return () => document.body.classList.remove("mm-lock");
  }, [openSidebar]);

  const getItemLabel = (item) => {
    const key = item.tKey || item.i18nKey; // support either prop name
    return key ? t(key) : item.label;
  };

  const renderLink = (item, className) => {
    const label = getItemLabel(item);
    if (item.path) {
      return (
        <RouterLink className={className} to={localeizePath(lang, item.path)} role="menuitem">
          {label}
        </RouterLink>
      );
    }
    if (item.href) {
      return (
        <a className={className} href={item.href} role="menuitem">
          {label}
        </a>
      );
    }
    if (item.to) {
      return (
        <ScrollLink
          to={item.to}
          className={className}
          smooth={true}
          offset={-150}
          role="menuitem"
        >
          {label}
        </ScrollLink>
      );
    }
    return (
      <span className={className} role="menuitem">
        {label}
      </span>
    );
  };

  useEffect(() => {
    // enable accordion behavior for mobile menu items (.mobileMenu__summary inside .mobileMenu__item)
    const summaries = Array.from(document.querySelectorAll(".mobileMenu__summary"));

    function toggleItem(summary) {
      const item = summary.closest(".mobileMenu__item");
      if (!item) return;
      const open = item.hasAttribute("open");
      if (open) {
        item.removeAttribute("open");
        summary.setAttribute("aria-expanded", "false");
      } else {
        item.setAttribute("open", "");
        summary.setAttribute("aria-expanded", "true");
      }
    }

    function onClick(e) {
      toggleItem(e.currentTarget);
    }

    function onKey(e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggleItem(e.currentTarget);
      }
    }

    summaries.forEach((s) => {
      s.setAttribute("role", "button");
      s.setAttribute("tabindex", "0");
      if (!s.hasAttribute("aria-expanded")) s.setAttribute("aria-expanded", "false");
      s.addEventListener("click", onClick);
      s.addEventListener("keydown", onKey);
    });

    return () => {
      summaries.forEach((s) => {
        s.removeEventListener("click", onClick);
        s.removeEventListener("keydown", onKey);
      });
    };
  }, []); // run once on mount

  return (
    <>
      <Sidebar openSidebar={openSidebar} onClose={() => setOpenSidebar(false)} />
      <PhoneModal isOpen={isPhoneModalOpen} onClose={() => setIsPhoneModalOpen(false)} />

      <nav className={`flex__center navbar ${
        showNavbar ? "drop" : ""
      } ${
        isMobile && showNavbar 
          ? isInHero 
            ? "mobile-blue" 
            : "mobile-white" 
          : ""
      }`}>
        <Logo />

        {/* Desktop links */}
        <div className="navbar__links" role="menubar" aria-label={t("nav.primary", { defaultValue: "Primary" })}>
          {navigations.map((item, idx) => (
            <div className="nav__group" key={idx}>
              {renderLink(item, "nav__item")}
              {Array.isArray(item.submenu) && item.submenu.length > 0 && (
                <div className="nav__dropdown" role="menu" aria-label={getItemLabel(item)}>
                  {item.submenu.map((sub, i) => {
                    const subLabel = getItemLabel(sub);
                    
                    // Handle special action (like opening phone modal)
                    if (sub.action === "openPhoneModal") {
                      return (
                        <button
                          key={i}
                          className="nav__dropdown-link"
                          onClick={(e) => {
                            e.preventDefault();
                            setIsPhoneModalOpen(true);
                          }}
                          role="menuitem"
                          type="button"
                        >
                          {subLabel}
                        </button>
                      );
                    }
                    
                    return sub.path ? (
                      <RouterLink
                        key={i}
                        to={localeizePath(lang, sub.path)}
                        className="nav__dropdown-link"
                        role="menuitem"
                      >
                        {subLabel}
                      </RouterLink>
                    ) : sub.href ? (
                      <a key={i} className="nav__dropdown-link" href={sub.href} role="menuitem">
                        {subLabel}
                      </a>
                    ) : (
                      <ScrollLink
                        key={i}
                        to={sub.to}
                        className="nav__dropdown-link"
                        smooth={true}
                        offset={-150}
                        role="menuitem"
                      >
                        {subLabel}
                      </ScrollLink>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Right-side controls */}
        <div className="flex__center">
          {/* Language switch (desktop) */}
          <div className="lang-switch" role="group" aria-label={t("lang.switch", { defaultValue: "Language" })}>
            <button
              type="button"
              className={`lang-btn ${lang === "en" ? "is-active" : ""}`}
              onClick={() => setLanguage("en")}
            >
              EN
            </button>
            <button
              type="button"
              className={`lang-btn ${lang === "fr" ? "is-active" : ""}`}
              onClick={() => setLanguage("fr")}
            >
              FR
            </button>
          </div>

          <button
            className="flex__center icon menu__btn"
            onClick={() => setOpenSidebar(true)}
            type="button"
          >
            <FiMenu 
              color={isMobile && showNavbar && !isInHero ? "#3244ba" : "#fff"} 
              size={28} 
            />
          </button>
        </div>
      </nav>

      {/* Mobile slide-in menu */}
      {openSidebar && (
        <div className="mobileMenu" role="dialog" aria-modal="true">
          <div className="mobileMenu__header">
            <span className="mobileMenu__title">{t("menu.title", { defaultValue: "Menu" })}</span>

            {/* Language switch (mobile) */}
            <div className="lang-switch lang-switch--mobile" role="group">
              <button
                type="button"
                className={`lang-btn ${lang === "en" ? "is-active" : ""}`}
                onClick={() => setLanguage("en")}
              >
                EN
              </button>
              <button
                type="button"
                className={`lang-btn ${lang === "fr" ? "is-active" : ""}`}
                onClick={() => setLanguage("fr")}
              >
                FR
              </button>
            </div>
          </div>

          <button
            className="mobileMenu__close"
            onClick={() => setOpenSidebar(false)}
            type="button"
          >
            <FiX size={32} />
          </button>

          <nav className="mobileMenu__nav" role="menu">
            {navigations.map((item, idx) => {
              const hasSub = Array.isArray(item.submenu) && item.submenu.length > 0;
              return (
                <details className="mobileMenu__item" key={idx}>
                  <summary className="mobileMenu__summary">
                    <span className="mobileMenu__label">
                      {item.path ? (
                        <RouterLink
                          to={localeizePath(lang, item.path)}
                          className="mobileMenu__link"
                          role="menuitem"
                          onClick={() => setOpenSidebar(false)}
                        >
                          {getItemLabel(item)}
                        </RouterLink>
                      ) : item.href ? (
                        <a
                          href={item.href}
                          className="mobileMenu__link"
                          role="menuitem"
                          onClick={() => setOpenSidebar(false)}
                        >
                          {getItemLabel(item)}
                        </a>
                      ) : item.to ? (
                        <ScrollLink
                          to={item.to}
                          className="mobileMenu__link"
                          smooth
                          offset={-150}
                          role="menuitem"
                          onClick={() => setOpenSidebar(false)}
                        >
                          {getItemLabel(item)}
                        </ScrollLink>
                      ) : (
                        <span className="mobileMenu__link">{getItemLabel(item)}</span>
                      )}
                    </span>

                    {/* visual toggle indicator */}
                    {hasSub && <span className="mobileMenu__toggle" aria-hidden />}
                  </summary>

                  {hasSub && (
                    <div className="mobileMenu__submenu" role="group" aria-label={getItemLabel(item)}>
                      {item.submenu.map((sub, i) => {
                        const subLabel = getItemLabel(sub);
                        
                        // Handle special action (like opening phone modal)
                        if (sub.action === "openPhoneModal") {
                          return (
                            <button
                              key={i}
                              className="mobileMenu__sublink"
                              onClick={(e) => {
                                e.preventDefault();
                                setIsPhoneModalOpen(true);
                                setOpenSidebar(false);
                              }}
                              role="menuitem"
                              type="button"
                            >
                              {subLabel}
                            </button>
                          );
                        }
                        
                        if (sub.path) {
                          return (
                            <RouterLink
                              key={i}
                              to={localeizePath(lang, sub.path)}
                              className="mobileMenu__sublink"
                              role="menuitem"
                              onClick={() => setOpenSidebar(false)}
                            >
                              {subLabel}
                            </RouterLink>
                          );
                        }
                        if (sub.href) {
                          return (
                            <a
                              key={i}
                              href={sub.href}
                              className="mobileMenu__sublink"
                              role="menuitem"
                              onClick={() => setOpenSidebar(false)}
                            >
                              {subLabel}
                            </a>
                          );
                        }
                        return (
                          <ScrollLink
                            key={i}
                            to={sub.to}
                            className="mobileMenu__sublink"
                            smooth
                            offset={-150}
                            role="menuitem"
                            onClick={() => setOpenSidebar(false)}
                          >
                            {subLabel}
                          </ScrollLink>
                        );
                      })}
                    </div>
                  )}
                </details>
              );
            })}
          </nav>
        </div>
      )}
    </>
  );
};

export default Navbar;